import React, { useCallback } from 'react';
import {
  CategoryConfigurationDefinitionResponse,
  ConfigurationFieldType,
  ConfigurationSectionType,
} from '@ydistri/api-sdk';
import SubconfigurationRow from '../components/components';
import ConfigurationRow from '../ConfigurationRow';
import { List } from 'antd';
import ConfigurationHfmqPairs from '../ConfigurationHfmqPairs/ConfigurationHfmqPairs';
import ConfigurationStrictSets from '../ConfigurationStrictSets/ConfigurationStrictSets';
import { ConditionalConfigurationCheckboxesValue } from '../../../components/ConditionalConfigurationContent/conditionalConfigurationContentTypes';
import ConditionalConfigurationContent from '../../../components/ConditionalConfigurationContent/ConditionalConfigurationContent';
import ConfigurationSection from '../../../components/ConfigurationSection/ConfigurationSection';
import ConfigurationRedistributionSetsIndicator from './ConfigurationRedistributionSetsIndicator';
import { computeRemSize } from '@ydistri/theme';

interface ConfigurationRedistributionSetsProps {
  sectionType: ConfigurationSectionType;
  data: CategoryConfigurationDefinitionResponse[];
  title?: string;
}

const hfmqPairsFieldTypes = [
  ConfigurationFieldType.PsHfmqPairs,
  ConfigurationFieldType.PsHfmqPairsForced,
];

const strictSetsFieldTypes = [
  ConfigurationFieldType.PsUseStrictSets,
  ConfigurationFieldType.PsUseStrictSetsForced,
];

const customRenderedFields = [
  ConfigurationFieldType.PsUseMinCostUnitPriceForSets,
  ConfigurationFieldType.PsMinCostUnitPriceForSets,
  ConfigurationFieldType.PsUseMinCostUnitPriceForSetsForced,
  ConfigurationFieldType.PsMinCostUnitPriceForSetsForced,
];

const hfmqEnabledFieldTypes: Partial<Record<ConfigurationSectionType, ConfigurationFieldType>> = {
  [ConfigurationSectionType.PotentialSourceStandardSubsection]: ConfigurationFieldType.PsUseHfmq,
  [ConfigurationSectionType.PotentialSourceForcedSubsection]:
    ConfigurationFieldType.PsUseHfmqForced,
};

const allowBreakingSetsMinQuantitiesEnabledFieldTypes: Record<
  ConfigurationSectionType,
  ConfigurationFieldType[]
> = {
  [ConfigurationSectionType.PotentialSourceStandardSubsection]: [
    ConfigurationFieldType.PsUseSmartSets,
    ConfigurationFieldType.PsUseStrictSets,
    ConfigurationFieldType.PsUseMinRedistributionQuantity,
    ConfigurationFieldType.PsUseHfmq,
  ],
  [ConfigurationSectionType.PotentialSourceForcedSubsection]: [
    ConfigurationFieldType.PsUseSmartSetsForced,
    ConfigurationFieldType.PsUseStrictSetsForced,
    ConfigurationFieldType.PsUseMinRedistributionQuantityForced,
    ConfigurationFieldType.PsUseHfmqForced,
  ],
  [ConfigurationSectionType.General]: [],
  [ConfigurationSectionType.PotentialTarget]: [],
  [ConfigurationSectionType.PotentialSource]: [],
};

const indicatorsFieldTypes: Partial<Record<ConfigurationSectionType, ConfigurationFieldType[]>> = {
  [ConfigurationSectionType.PotentialSourceStandardSubsection]: [
    ConfigurationFieldType.PsUseSmartSets,
    ConfigurationFieldType.PsUseStrictSets,
    ConfigurationFieldType.PsUseMinRedistributionQuantity,
    ConfigurationFieldType.PsUseHfmq,
    ConfigurationFieldType.PsAllowBreakSetsMinQuantitiesWhenGoingToZero,
  ],
  [ConfigurationSectionType.PotentialSourceForcedSubsection]: [
    ConfigurationFieldType.PsUseSmartSetsForced,
    ConfigurationFieldType.PsUseStrictSetsForced,
    ConfigurationFieldType.PsUseMinRedistributionQuantityForced,
    ConfigurationFieldType.PsUseHfmqForced,
    ConfigurationFieldType.PsAllowBreakSetsMinQuantitiesWhenGoingToZeroForced,
  ],
};

const isHfmqPairsFieldType = (fieldType: ConfigurationFieldType) => {
  return hfmqPairsFieldTypes.includes(fieldType);
};

const isStrictSetsFieldType = (fieldType: ConfigurationFieldType) => {
  return strictSetsFieldTypes.includes(fieldType);
};

const isCustomRenderedFieldType = (fieldType: ConfigurationFieldType) => {
  return customRenderedFields.includes(fieldType);
};

const isAllowBreakSetsMinQuantitiesFieldType = (fieldType: ConfigurationFieldType) => {
  return (
    fieldType === ConfigurationFieldType.PsAllowBreakSetsMinQuantitiesWhenGoingToZeroForced ||
    fieldType === ConfigurationFieldType.PsAllowBreakSetsMinQuantitiesWhenGoingToZero
  );
};

const SINGLE_LAYOUT_CONTROLS_COLUMN_WIDTH = 360;

const ConfigurationRedistributionSets: React.FC<ConfigurationRedistributionSetsProps> = ({
  data,
  sectionType,
  title = 'Redistribution Sets Configuration',
}) => {
  const filteredData = data.filter(definition => {
    // fields rendered in custom way are excluded to avoid empty divs
    return !isCustomRenderedFieldType(definition.fieldType);
  });

  const hfmqFieldType = hfmqEnabledFieldTypes[sectionType];
  const indicatorsFields = indicatorsFieldTypes[sectionType];
  const indicatorsDefinitionData = data.filter(definition => {
    return indicatorsFields?.includes(definition.fieldType);
  });

  const renderDefinition = useCallback(
    (definition: CategoryConfigurationDefinitionResponse) => {
      const getRowContent = () => {
        if (isHfmqPairsFieldType(definition.fieldType)) {
          if (hfmqFieldType) {
            return (
              <ConditionalConfigurationContent
                fieldTypes={[hfmqFieldType]}
                renderWhenValue={ConditionalConfigurationCheckboxesValue.ALL_ON}
              >
                <ConfigurationRow definition={definition} layout="children">
                  <ConfigurationHfmqPairs definition={definition} />
                </ConfigurationRow>
              </ConditionalConfigurationContent>
            );
          } else {
            return null;
          }
        } else if (isStrictSetsFieldType(definition.fieldType)) {
          return (
            <ConfigurationRow
              definition={definition}
              layout="children"
              childrenJustifyContent="flex-start"
            >
              <ConfigurationStrictSets definitions={data} sectionType={sectionType} />
            </ConfigurationRow>
          );
        } else if (isAllowBreakSetsMinQuantitiesFieldType(definition.fieldType)) {
          return (
            <ConditionalConfigurationContent
              fieldTypes={allowBreakingSetsMinQuantitiesEnabledFieldTypes[sectionType]}
              renderWhenValue={ConditionalConfigurationCheckboxesValue.AT_LEAST_ONE_ON}
            >
              <ConfigurationRow
                definition={definition}
                layout="single"
                controlsColumnWidth={computeRemSize(SINGLE_LAYOUT_CONTROLS_COLUMN_WIDTH)}
                childrenJustifyContent="flex-start"
              />
            </ConditionalConfigurationContent>
          );
        } else {
          return (
            <ConfigurationRow
              definition={definition}
              layout="single"
              controlsColumnWidth={computeRemSize(SINGLE_LAYOUT_CONTROLS_COLUMN_WIDTH)}
              childrenJustifyContent="flex-start"
            />
          );
        }
      };

      return <SubconfigurationRow key={definition.id}>{getRowContent()}</SubconfigurationRow>;
    },
    [data, hfmqFieldType, sectionType],
  );
  return (
    <ConfigurationSection
      title={title}
      id={`ConfigurationRedistributionSets-${sectionType}`}
      collapsedTitleAdditionalContent={
        <ConfigurationRedistributionSetsIndicator
          definitionData={indicatorsDefinitionData}
          width={computeRemSize(SINGLE_LAYOUT_CONTROLS_COLUMN_WIDTH)}
        />
      }
    >
      <List
        dataSource={filteredData}
        loading={false}
        size="small"
        renderItem={renderDefinition}
        data-testid={`ConfigurationRedistributionSets-${sectionType}`}
      />
    </ConfigurationSection>
  );
};

export default ConfigurationRedistributionSets;
